import React, { FC, useMemo, useState } from 'react';
import { View, TextInput } from 'react-native';
import i18n from 'i18n-js';
import { connect } from 'react-redux';

import { scale as initialScale } from '@components/frame';
import { font as initialFont } from '@styles/fonts';
import { isWeb } from '@helpers';
import TouchableElement from '@components/common/touchableElement';

import Svg from '@styles/icons/svg';
import { ColorFunction } from '@styles/colors/types';
import { ScaleFunction } from '@components/operatorPanel/interfaces';
import { SCREEN_BORDER_RADIUS, TEST_IDS } from '@v2/pages/common/constants/base.constants';

export interface SearchInputProps {
  C: ColorFunction;
  searchValue: string;
  onFocus: () => void;
  searchRef: React.RefObject<TextInput>;
  onSearch: (v: string) => any;
  cancelAnimation: () => any;
  submit?: (v: string) => void;
  inputName?: string;
  onBlur: () => void;
  bg?: string;
  isPopupMode?: boolean;
  scale?: ScaleFunction;
  font?: typeof initialFont;
}

const SearchInput: FC<SearchInputProps> = ({
  C,
  searchValue,
  onFocus,
  onSearch,
  submit,
  inputName,
  onBlur,
  bg = 'Gray/Light/5',
  cancelAnimation,
  searchRef,
  isPopupMode = false,
  scale = initialScale,
  font = initialFont,
}) => {
  const [wasSubmitted, setWasSubmitted] = useState(false);

  const searchParamInURL = useMemo(() => {
    if (!isWeb) return null;
    const urlParams = new URLSearchParams(window?.location?.search);
    return urlParams.get('search');
  }, [isWeb, window?.location?.search]);

  return (
    <View style={{ flex: 1, maxWidth: '100%', height: scale(36) }}>
      <View style={{ position: 'absolute', left: isPopupMode ? scale(6) : scale(8), top: scale(10), zIndex: 10000 }}>
        <Svg icon="search" color="Label/Light/Tertiary" width={scale(16)} height={scale(16)} />
      </View>
      {Boolean(searchValue) && (
        <View style={{ position: 'absolute', right: isPopupMode ? scale(6) : scale(8), top: scale(10), zIndex: 10000 }}>
          <TouchableElement
            testID={TEST_IDS.searchScreen.clearSearchButton}
            onPress={() => {
              if (wasSubmitted || (isWeb && searchParamInURL)) {
                cancelAnimation?.();
              } else {
                onSearch('');
              }
              searchRef?.current?.focus();
            }}
          >
            {({ boxStyle }) => (
              <View style={[boxStyle]}>
                <Svg icon="clear" color="Label/Light/Tertiary" width={scale(16)} height={scale(16)} />
              </View>
            )}
          </TouchableElement>
        </View>
      )}
      <View>
        <TextInput
          style={[
            {
              width: '100%',
              height: scale(36),
              backgroundColor: C(bg),
              color: C('Label/Dark/Primary'),
              borderRadius: scale(SCREEN_BORDER_RADIUS),
              paddingLeft: isPopupMode ? scale(28) : scale(31),
              paddingRight: isPopupMode ? scale(6) : scale(8),
              paddingTop: 0,
              paddingBottom: 0,
              textAlignVertical: 'center',
              fontSize: scale(16),
              zIndex: 2,
            },
          ]}
          maxLength={100}
          placeholderTextColor={C('Label/Light/Tertiary')}
          placeholder={i18n.t(inputName ? `Search ${inputName}` : 'Search')}
          value={searchValue}
          onChangeText={(t) => {
            onSearch(t);
          }}
          onFocus={() => {
            setWasSubmitted(false);
            onFocus();
          }}
          onBlur={() => {
            onBlur();
          }}
          ref={searchRef}
          onSubmitEditing={(e) => {
            setWasSubmitted(true);
            if (submit) {
              submit(searchValue);
            }
          }}
          returnKeyType="search"
        />
      </View>
    </View>
  );
};

const mapStateToProps = ({ manufacture: { ncFiles }, parts }: any) => ({ ncFiles, parts });

export default connect(mapStateToProps, {})(SearchInput);
